import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FilterSelect } from "@/components/ui/filter-select";
import { api } from "@/lib/api";
import { BTN_PAGE_PRIMARY_ACTION } from "@/lib/buttonStyles";
import { PageContentLoader } from "@/components/PageContentLoader";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { PAGE_TITLE_CLASS } from "@/lib/modalStyles";
import { toast } from "@/lib/appToast";
import InputMask from "react-input-mask";
import { buildFormDraftKey, clearFormDraft, loadFormDraft, saveFormDraft } from "@/lib/formDraft";

const HORARIOS_INTEIROS = Array.from({ length: 24 }, (_, h) => {
  const label = `${String(h).padStart(2, "0")}:00`;
  return label;
});

type MeuPerfilPageProps = {
  /** Exibe e envia Dados da Empresa (somente Admin). */
  showEmpresaSection?: boolean;
  /** Oculta o título da página quando embutido em Configurações. */
  embedded?: boolean;
};

type PerfilFormSnapshot = {
  nome: string;
  email: string;
  cpf: string;
  celular: string;
  nomeEmpresa: string;
  cnpj: string;
  ie: string;
  rua: string;
  numero: string;
  bairro: string;
  cidade: string;
  cep: string;
  cmvIdeal: string;
  horarioAbertura: string;
  horarioEncerramento: string;
};

function cmvFromApi(value: number | null): string {
  return value != null ? String(value).replace(".", ",") : "";
}

function perfilSnapshotsEqual(
  a: PerfilFormSnapshot,
  b: PerfilFormSnapshot,
  includeEmpresa: boolean,
): boolean {
  if (a.nome !== b.nome || a.email !== b.email || a.cpf !== b.cpf || a.celular !== b.celular) {
    return false;
  }
  if (!includeEmpresa) return true;
  return (
    a.nomeEmpresa === b.nomeEmpresa &&
    a.cnpj === b.cnpj &&
    a.ie === b.ie &&
    a.rua === b.rua &&
    a.numero === b.numero &&
    a.bairro === b.bairro &&
    a.cidade === b.cidade &&
    a.cep === b.cep &&
    a.cmvIdeal === b.cmvIdeal &&
    a.horarioAbertura === b.horarioAbertura &&
    a.horarioEncerramento === b.horarioEncerramento
  );
}

export default function MeuPerfilPage({ showEmpresaSection = true, embedded = false }: MeuPerfilPageProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [originalSnapshot, setOriginalSnapshot] = useState<PerfilFormSnapshot | null>(null);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");
  const [celular, setCelular] = useState("");
  const [nomeEmpresa, setNomeEmpresa] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [ie, setIe] = useState("");
  const [rua, setRua] = useState("");
  const [numero, setNumero] = useState("");
  const [bairro, setBairro] = useState("");
  const [cidade, setCidade] = useState("");
  const [cep, setCep] = useState("");
  const [cmvIdeal, setCmvIdeal] = useState("");
  const [horarioAbertura, setHorarioAbertura] = useState("");
  const [horarioEncerramento, setHorarioEncerramento] = useState("");
  const draftKey = useMemo(
    () => buildFormDraftKey("meu-perfil", showEmpresaSection ? "empresa" : "pessoal"),
    [showEmpresaSection],
  );

  const currentSnapshot = useMemo<PerfilFormSnapshot>(
    () => ({
      nome,
      email,
      cpf,
      celular,
      nomeEmpresa,
      cnpj,
      ie,
      rua,
      numero,
      bairro,
      cidade,
      cep,
      cmvIdeal,
      horarioAbertura,
      horarioEncerramento,
    }),
    [
      nome,
      email,
      cpf,
      celular,
      nomeEmpresa,
      cnpj,
      ie,
      rua,
      numero,
      bairro,
      cidade,
      cep,
      cmvIdeal,
      horarioAbertura,
      horarioEncerramento,
    ],
  );

  const isDirty =
    originalSnapshot !== null &&
    !perfilSnapshotsEqual(currentSnapshot, originalSnapshot, showEmpresaSection);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const p = await api.obterMeuPerfil();
        const loaded: PerfilFormSnapshot = {
          nome: p.nome ?? "",
          email: p.email ?? "",
          cpf: p.cpf ?? "",
          celular: p.celular ?? "",
          nomeEmpresa: p.nome_empresa ?? "",
          cnpj: p.cnpj ?? "",
          ie: p.ie ?? "",
          rua: p.rua ?? "",
          numero: p.numero ?? "",
          bairro: p.bairro ?? "",
          cidade: p.cidade ?? "",
          cep: p.cep ?? "",
          cmvIdeal: cmvFromApi(p.cmv_ideal),
          horarioAbertura: p.horario_abertura ?? "",
          horarioEncerramento: p.horario_encerramento ?? "",
        };
        setOriginalSnapshot(loaded);
        const draft = loadFormDraft<PerfilFormSnapshot>(draftKey) ?? loaded;
        setNome(draft.nome);
        setEmail(draft.email);
        setCpf(draft.cpf);
        setCelular(draft.celular);
        setNomeEmpresa(draft.nomeEmpresa);
        setCnpj(draft.cnpj);
        setIe(draft.ie);
        setRua(draft.rua);
        setNumero(draft.numero);
        setBairro(draft.bairro);
        setCidade(draft.cidade);
        setCep(draft.cep);
        setCmvIdeal(draft.cmvIdeal);
        setHorarioAbertura(draft.horarioAbertura);
        setHorarioEncerramento(draft.horarioEncerramento);
      } catch (e) {
        console.error(e);
        toast.error("Erro ao carregar perfil");
      } finally {
        setLoading(false);
      }
    })();
  }, [draftKey]);

  useEffect(() => {
    if (loading || originalSnapshot === null) return;
    if (!isDirty) {
      clearFormDraft(draftKey, { suppressAutosave: false });
      return;
    }
    saveFormDraft(draftKey, currentSnapshot);
  }, [currentSnapshot, draftKey, loading, originalSnapshot, isDirty]);

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.salvarMeuPerfil({
        nome: nome.trim() || null,
        email: email.trim() || null,
        cpf: cpf.trim() || null,
        celular: celular.trim() || null,
        ...(showEmpresaSection
          ? {
              nome_empresa: nomeEmpresa.trim() || null,
              cnpj: cnpj.trim() || null,
              ie: ie.trim() || null,
              rua: rua.trim() || null,
              numero: numero.trim() || null,
              bairro: bairro.trim() || null,
              cidade: cidade.trim() || null,
              cep: cep.trim() || null,
              cmv_ideal: cmvIdeal.trim() === "" ? null : cmvIdeal.trim(),
              horario_abertura: horarioAbertura.trim() || null,
              horario_encerramento: horarioEncerramento.trim() || null,
            }
          : {}),
      });
      toast.success("Perfil salvo.");
      clearFormDraft(draftKey);
      setOriginalSnapshot(currentSnapshot);
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : "Erro ao salvar perfil");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageContentLoader />;
  }

  return (
    <div className={embedded ? "w-full" : "mx-auto max-w-3xl space-y-6"}>
      {!embedded ? (
        <h1 className={PAGE_TITLE_CLASS}>Meu Perfil</h1>
      ) : null}
      <Card className="border border-gray-200 bg-white shadow-sm">
        <CardContent className="space-y-8 !p-6 sm:!p-8">
          <form onSubmit={handleSalvar} className="space-y-8">
            <section className="space-y-4">
              <h2 className="text-base font-semibold text-foreground">Dados pessoais</h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="perfil_nome">Nome</Label>
                  <Input id="perfil_nome" value={nome} onChange={(e) => setNome(e.target.value)} className="border-gray-200" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="perfil_email">E-mail</Label>
                  <Input
                    id="perfil_email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="border-gray-200"
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="perfil_cpf">CPF</Label>
                    <InputMask
                      mask="999.999.999-99"
                      value={cpf}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCpf(e.target.value)}
                    >
                      {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                        <Input {...inputProps} id="perfil_cpf" className="border-gray-200" />
                      )}
                    </InputMask>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perfil_celular">Celular</Label>
                    <InputMask
                      mask="(99) 9 9999-9999"
                      value={celular}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCelular(e.target.value)}
                    >
                      {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                        <Input {...inputProps} id="perfil_celular" className="border-gray-200" />
                      )}
                    </InputMask>
                  </div>
                </div>
              </div>
            </section>

            {showEmpresaSection ? (
              <section className="space-y-4">
                <h2 className="text-base font-semibold text-foreground">Dados da Empresa</h2>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="perfil_empresa">Nome da Empresa</Label>
                    <Input
                      id="perfil_empresa"
                      value={nomeEmpresa}
                      onChange={(e) => setNomeEmpresa(e.target.value)}
                      className="border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perfil_cnpj">CNPJ</Label>
                    <InputMask
                      mask="99.999.999/9999-99"
                      value={cnpj}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCnpj(e.target.value)}
                    >
                      {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                        <Input {...inputProps} id="perfil_cnpj" className="border-gray-200" />
                      )}
                    </InputMask>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perfil_ie">IE</Label>
                    <Input
                      id="perfil_ie"
                      value={ie}
                      onChange={(e) => setIe(e.target.value)}
                      className="border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perfil_cep">CEP</Label>
                    <InputMask
                      mask="99999-999"
                      value={cep}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCep(e.target.value)}
                    >
                      {(inputProps: React.InputHTMLAttributes<HTMLInputElement>) => (
                        <Input {...inputProps} id="perfil_cep" className="border-gray-200" />
                      )}
                    </InputMask>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="perfil_rua">Rua</Label>
                    <Input
                      id="perfil_rua"
                      value={rua}
                      onChange={(e) => setRua(e.target.value)}
                      className="border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perfil_numero">Número</Label>
                    <Input
                      id="perfil_numero"
                      value={numero}
                      onChange={(e) => setNumero(e.target.value)}
                      className="border-gray-200"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="perfil_bairro">Bairro</Label>
                    <Input
                      id="perfil_bairro"
                      value={bairro}
                      onChange={(e) => setBairro(e.target.value)}
                      className="border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perfil_cidade">Cidade</Label>
                    <Input
                      id="perfil_cidade"
                      value={cidade}
                      onChange={(e) => setCidade(e.target.value)}
                      className="border-gray-200"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="perfil_cmv">CMV Ideal</Label>
                    <Input
                      id="perfil_cmv"
                      inputMode="decimal"
                      value={cmvIdeal}
                      onChange={(e) => setCmvIdeal(e.target.value)}
                      placeholder="Ex: 30"
                      className="border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perfil_horario_abertura">Horário de abertura</Label>
                    <FilterSelect
                      id="perfil_horario_abertura"
                      value={horarioAbertura}
                      onChange={(e) => setHorarioAbertura(e.target.value)}
                      placeholder="Selecione…"
                      className="w-full border-gray-200 bg-white"
                      aria-label="Horário de abertura"
                    >
                      {HORARIOS_INTEIROS.map((h) => (
                        <option key={`abertura-${h}`} value={h}>
                          {h}
                        </option>
                      ))}
                    </FilterSelect>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="perfil_horario_encerramento">Horário de encerramento</Label>
                    <FilterSelect
                      id="perfil_horario_encerramento"
                      value={horarioEncerramento}
                      onChange={(e) => setHorarioEncerramento(e.target.value)}
                      placeholder="Selecione…"
                      className="w-full border-gray-200 bg-white"
                      aria-label="Horário de encerramento"
                    >
                      {HORARIOS_INTEIROS.map((h) => (
                        <option key={`encerramento-${h}`} value={h}>
                          {h}
                        </option>
                      ))}
                    </FilterSelect>
                  </div>
                </div>
              </section>
            ) : null}

            <div className="flex justify-end pt-2">
              {isDirty ? (
                <Button type="submit" className={BTN_PAGE_PRIMARY_ACTION} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" /> : "Salvar"}
                </Button>
              ) : null}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
